import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type UserMe } from "@/api/client";
import Header from "@/components/Layout/Header";
import { usePublicConfig } from "@/lib/publicConfig";
import { useAuthStore } from "@/stores/authStore";

const STATUS_FILTER_VALUES = ["pending", "active", "rejected", ""] as const;

const STATUS_FILTER_KEY: Record<string, string> = {
  pending: "admin.filter.pending",
  active: "admin.filter.active",
  rejected: "admin.filter.rejected",
  "": "admin.filter.all",
};

const STATUS_BADGE: Record<string, string> = {
  pending: "bg-verdict-amber-bg text-verdict-amber border-verdict-amber/30",
  active: "bg-verdict-green-bg text-verdict-green border-verdict-green/30",
  rejected: "bg-verdict-red-bg text-verdict-red border-verdict-red/30",
  archived: "text-ca-surface-muted border-ca-surface-border",
};

export default function Admin() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const [filter, setFilter] = useState<string>("pending");
  const [users, setUsers] = useState<UserMe[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Freigeschaltet wird in Durchläufen gedacht, gebucht wird in Credits. Die
  // Umrechnung stand bis 19.8.2026 nirgends: der Prompt schlug „5" vor und
  // buchte 5 Credits — einen halben Durchlauf statt fünf.
  const cfg = usePublicConfig();
  const proAudit = cfg?.credit_costs?.audit ?? 10;

  const load = async (status: string) => {
    setLoading(true);
    setErr(null);
    try {
      const list = await api.adminListUsers(status || undefined);
      setUsers(list);
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(filter);
  }, [filter]);

  const replace = (u: UserMe) =>
    setUsers((prev) => prev.map((x) => (x.id === u.id ? u : x)));

  const approve = async (u: UserMe) => {
    const raw = prompt(
      t("admin.approvePrompt", {
        name: `${u.firstname} ${u.lastname}`,
        email: u.email,
        cost: proAudit,
      }),
      "5",
    );
    if (raw === null) return;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 0) return;
    try {
      const updated = await api.adminApproveUser(u.id, n * proAudit);
      replace(updated);
    } catch (e) {
      alert(String(e));
    }
  };

  const setBudget = async (u: UserMe) => {
    const raw = prompt(
      t("admin.budgetPrompt", { email: u.email, cost: proAudit }),
      String(u.audit_balance),
    );
    if (raw === null) return;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 0) return;
    try {
      const updated = await api.adminSetBudget(u.id, n);
      replace(updated);
    } catch (e) {
      alert(String(e));
    }
  };

  const reject = async (u: UserMe) => {
    if (!confirm(t("admin.rejectConfirm", { email: u.email }))) return;
    try {
      const updated = await api.adminRejectUser(u.id);
      replace(updated);
    } catch (e) {
      alert(String(e));
    }
  };

  if (me?.role !== "admin") {
    return (
      <>
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-12 text-ca-ink">
          {t("admin.onlyAdmins")}
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-black text-ca-ink tracking-tighter uppercase italic mb-2">
          {t("admin.title")}
        </h1>
        <p className="text-ca-ink-muted mb-8">
          {t("admin.subtitle")}
        </p>

        <div className="flex gap-2 mb-6">
          {STATUS_FILTER_VALUES.map((value) => (
            <button
              key={value || "all"}
              onClick={() => setFilter(value)}
              className={`px-4 py-2 text-xs font-black uppercase tracking-widest rounded-xl transition ${
                filter === value
                  ? "bg-ca-accent text-ca-brand"
                  : "bg-ca-card border border-ca-surface-border text-ca-surface-muted hover:text-ca-surface-ink"
              }`}
            >
              {t(STATUS_FILTER_KEY[value])}
            </button>
          ))}
        </div>

        {err && <p className="text-red-400 text-sm mb-4">{err}</p>}
        {loading ? (
          <p className="text-ca-ink-muted">{t("common.loadingEllipsis")}</p>
        ) : users.length === 0 ? (
          <p className="text-ca-ink-muted italic">{t("admin.noEntries")}</p>
        ) : (
          <div className="grid gap-3">
            {users.map((u) => (
              <div
                key={u.id}
                className="p-5 bg-ca-card rounded-2xl border border-ca-surface-border flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-ca-surface-ink font-bold truncate">
                      {u.firstname} {u.lastname}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-widest rounded-full border ${
                        STATUS_BADGE[u.status] || "text-ca-surface-muted border-ca-surface-border"
                      }`}
                    >
                      {u.status}
                    </span>
                    {u.role === "admin" && (
                      <span className="px-2 py-0.5 text-[10px] font-black uppercase tracking-widest rounded-full border border-ca-surface-accent/30 text-ca-surface-accent">
                        admin
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-ca-surface-muted font-mono truncate">
                    {u.email}
                  </p>
                  <p className="text-xs text-ca-surface-muted mt-1 truncate">
                    {[u.company, u.position].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-ca-surface-muted">
                    <span className="text-ca-surface-ink font-bold text-base">
                      {u.audit_balance}
                    </span>
                    {" / "}
                    <span className="text-ca-surface-muted">
                      {t("admin.used", { count: u.audit_lifetime_used })}
                    </span>
                  </span>
                  {u.status === "pending" && (
                    <>
                      <button
                        onClick={() => approve(u)}
                        className="px-3 py-2 bg-ca-accent text-ca-brand text-xs font-black uppercase tracking-widest rounded-lg hover:scale-105 transition"
                      >
                        {t("admin.approve")}
                      </button>
                      <button
                        onClick={() => reject(u)}
                        className="px-3 py-2 border border-ca-surface-border text-ca-surface-muted hover:text-verdict-red text-xs font-black uppercase tracking-widest rounded-lg transition"
                      >
                        {t("admin.reject")}
                      </button>
                    </>
                  )}
                  {u.status === "active" && (
                    <button
                      onClick={() => setBudget(u)}
                      className="px-3 py-2 border border-ca-surface-border text-ca-surface-ink hover:text-ca-surface-accent text-xs font-black uppercase tracking-widest rounded-lg transition"
                    >
                      {t("admin.budget")}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
